/* ============================================================
   Dathent — Public referral welcome (/r/:code/welcome)
   Confetti + "you're on the list".
   Source: design/Welcome.html
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import ThemeToggle from '../components/AppShell/ThemeToggle.jsx';

const COPY = {
  en: {
    status: 'Closed beta · your spot is reserved',
    headL1: "You're on the list —",
    headAccent: 'welcome to Dathent.',
    body1: 'Thanks for joining through',
    body2: "We're polishing the last edges of the beta and going public",
    body3: 'in the coming weeks',
    body4: ". We'll send you a single, well-mannered ping the moment we open the door.",
    notifyTitle: "We'll notify you at",
    notifyAnd: 'and',
    notifySub: 'One message · launch only · no marketing.',
    invitedBy: 'Invited by',
    tlH: 'What happens next',
    tlNowL: 'Today — your spot is locked in',
    tlNowS: "You're on the closed beta list. +2 months bonus will be available after subscription.",
    tlSoonL: 'In the coming weeks — public launch',
    tlSoonS: "We'll ping you the moment we open access. Your perks are pre-attached.",
    tlLaterL: 'Day one — onboarding + first batch',
    tlLaterS: '5-minute chat onboarding, then we generate your first 30 posts.',
    ctaPrimary: 'Read the landing page',
    ctaGhost: 'Back to home',
    notFound: 'Referral link not found',
  },
  uk: {
    status: 'Closed beta · твоє місце зарезервовано',
    headL1: 'Ти у списку —',
    headAccent: 'вітаємо в Dathent.',
    body1: 'Дякуємо за реєстрацію через',
    body2: 'Ми завершуємо beta і відкриваємось публічно',
    body3: 'в найближчі тижні',
    body4: '. Надішлемо одне акуратне повідомлення в момент відкриття.',
    notifyTitle: 'Повідомимо тебе на',
    notifyAnd: 'та',
    notifySub: 'Одне повідомлення · тільки запуск · без маркетингу.',
    invitedBy: 'Запросив',
    tlH: 'Що буде далі',
    tlNowL: 'Сьогодні — місце за тобою',
    tlNowS: 'Ти в списку closed beta. Бонус +2 місяці буде доступний після підписки.',
    tlSoonL: 'У найближчі тижні — публічний запуск',
    tlSoonS: 'Напишемо в момент відкриття. Бонуси чекають на тебе.',
    tlLaterL: 'День перший — онбординг + перші пости',
    tlLaterS: '5-хвилинний онбординг, потім згенеруємо перші 30 постів.',
    ctaPrimary: 'Читати лендінг',
    ctaGhost: 'Назад',
    notFound: 'Реферальне посилання не знайдено',
  },
  ru: {
    status: 'Closed beta · твоё место зарезервировано',
    headL1: 'Ты в списке —',
    headAccent: 'добро пожаловать в Dathent.',
    body1: 'Спасибо за регистрацию через',
    body2: 'Заканчиваем бета-тест и открываемся публично',
    body3: 'в ближайшие недели',
    body4: '. Пришлём одно сообщение в момент открытия.',
    notifyTitle: 'Уведомим на',
    notifyAnd: 'и',
    notifySub: 'Одно сообщение · только о запуске · без рассылки.',
    invitedBy: 'Пригласил',
    tlH: 'Что дальше',
    tlNowL: 'Сегодня — место за тобой',
    tlNowS: 'Ты в списке closed beta. Бонус +2 месяца будет доступен после подписки.',
    tlSoonL: 'В ближайшие недели — публичный запуск',
    tlSoonS: 'Напишем в момент открытия. Бонусы уже ждут.',
    tlLaterL: 'Первый день — онбординг + первые посты',
    tlLaterS: '5-минутный онбординг, затем сгенерируем первые 30 постов.',
    ctaPrimary: 'Открыть лендинг',
    ctaGhost: 'Назад',
    notFound: 'Реферальная ссылка не найдена',
  },
};

function detectLocale() {
  if (typeof navigator === 'undefined') return 'en';
  const lang = (navigator.language || 'en').slice(0, 2).toLowerCase();
  if (['uk', 'ru', 'en'].includes(lang)) return lang;
  return 'en';
}

const DEFAULT_GRADIENT = ['#D9CBB3', '#766858'];

export function ReferralWelcome() {
  const { code } = useParams();
  const [referrer, setReferrer] = useState(null);
  const [stored, setStored] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [locale] = useState(detectLocale());
  const t = COPY[locale] || COPY.en;

  useEffect(() => {
    let raw = null;
    try {
      raw = JSON.parse(sessionStorage.getItem('dathent.referralSignup') || 'null');
    } catch {
      raw = null;
    }
    if (raw && raw.referrer) {
      setReferrer(raw.referrer);
      setStored(raw);
    } else {
      api.referral.lookupByCode(code).then((res) => {
        if (res?.referrer) setReferrer(res.referrer);
        else setNotFound(true);
      }).catch(() => setNotFound(true));
    }
  }, [code]);

  const confetti = useMemo(() => {
    const palette = ['#4FC3FF', '#7A5BFF', '#E455CF', '#F5B544', '#15803D', '#0A66C2'];
    const out = [];
    for (let i = 0; i < 60; i++) {
      out.push({
        id: i,
        color: palette[Math.floor(Math.random() * palette.length)],
        left: Math.random() * 100,
        tx: `${Math.random() * 200 - 100}px`,
        rot: `${Math.random() * 720 + 360}deg`,
        delay: `${(Math.random() * 1.4).toFixed(2)}s`,
        dur: `${(2.6 + Math.random() * 2.4).toFixed(2)}s`,
        width: `${(5 + Math.random() * 6).toFixed(1)}px`,
        height: `${(8 + Math.random() * 8).toFixed(1)}px`,
        radius: Math.random() > 0.6 ? '999px' : '2px',
      });
    }
    return out;
  }, []);

  if (notFound) {
    return (
      <div className="w-page">
        <div style={{ padding: '120px 24px', textAlign: 'center' }}>
          <h1 className="w-h">{t.notFound}</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>
            <Link to="/">← Dathent</Link>
          </p>
        </div>
      </div>
    );
  }

  const grad = referrer?.avatarGradient || DEFAULT_GRADIENT;
  const email = stored?.email || '';
  const contact = stored?.contactValue || '';
  const recipients = [email, contact].filter(Boolean);

  return (
    <div className="w-page">
      <header className="w-top">
        <Link to="/" className="w-brand">
          <img src="/assets/dathent-logo.png" alt="" width={28} height={28} />
          <span>Dathent</span>
        </Link>
        <div style={{ flex: 1 }} />
        <ThemeToggle />
      </header>

      <main className="w-shell">
        <div className="w-bg" />
        <div className="w-confetti">
          {confetti.map((c) => (
            <span
              key={c.id}
              style={{
                left: `${c.left}%`,
                background: c.color,
                width: c.width,
                height: c.height,
                borderRadius: c.radius,
                '--tx': c.tx,
                '--rot': c.rot,
                '--delay': c.delay,
                '--d': c.dur,
              }}
            />
          ))}
        </div>

        <div className="w-medallion">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 12 5 5 9-11" />
          </svg>
        </div>

        <span className="w-status">
          <span className="w-status-dot" />
          {t.status}
        </span>

        <h1 className="w-h">
          {t.headL1}<br />
          <span className="ac">{t.headAccent}</span>
        </h1>

        <p className="w-p">
          {t.body1} <b>{referrer?.displayName || 'a friend'}</b>. {t.body2} <b>{t.body3}</b>{t.body4}
        </p>

        {recipients.length > 0 && (
          <div className="w-notify">
            <span className="w-notify-ic">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" />
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              </svg>
            </span>
            <div className="w-notify-text">
              <div className="w-notify-l">
                {t.notifyTitle}{' '}
                {recipients.map((r, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span> {t.notifyAnd} </span>}
                    <b>{r}</b>
                  </React.Fragment>
                ))}
              </div>
              <div className="w-notify-s">{t.notifySub}</div>
            </div>
          </div>
        )}

        {referrer && (
          <span className="w-inviter">
            {referrer.photoUrl ? (
              <img
                className="w-inviter-av"
                src={referrer.photoUrl}
                alt=""
                style={{ objectPosition: referrer.photoPosition || '50% 50%' }}
              />
            ) : (
              <span
                className="w-inviter-av"
                style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
              >
                {(referrer.displayName || 'A').slice(0, 1).toUpperCase()}
              </span>
            )}
            <span>{t.invitedBy} <b>{referrer.displayName}{referrer.studio ? ` · ${referrer.studio}` : ''}</b></span>
          </span>
        )}

        <div className="w-timeline">
          <div className="w-tl-h">{t.tlH}</div>

          <div className="w-tl-step w-tl-now">
            <span className="w-tl-ico">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m5 12 5 5 9-11" />
              </svg>
            </span>
            <div>
              <div className="w-tl-l">{t.tlNowL}</div>
              <div className="w-tl-s">{t.tlNowS}</div>
            </div>
          </div>

          <div className="w-tl-step w-tl-soon">
            <span className="w-tl-ico">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </span>
            <div>
              <div className="w-tl-l">{t.tlSoonL}</div>
              <div className="w-tl-s">{t.tlSoonS}</div>
            </div>
          </div>

          <div className="w-tl-step w-tl-later">
            <span className="w-tl-ico">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" />
              </svg>
            </span>
            <div>
              <div className="w-tl-l">{t.tlLaterL}</div>
              <div className="w-tl-s">{t.tlLaterS}</div>
            </div>
          </div>
        </div>

        <div className="w-cta-row">
          <a className="w-cta w-cta-primary" href="https://dathent.ai/">
            {t.ctaPrimary}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </main>
    </div>
  );
}

export default ReferralWelcome;
